import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { toast } from 'react-toastify';

import { randomString } from 'vcs-common';
import dayjs from 'dayjs';

import {
  useChangeClientMutation,
  useCreateClientMutation,
  useDeleteConsultationMutation,
  useGetClientsQuery,
  useGetConsultationsQuery,
} from '../services/users.api';
import { useAppSelector } from '../hooks/redux';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { usePosthogCapture } from '../hooks/usePosthogCapture';
import { selectUser } from '../store/auth.slice';

import { getDateForFilter, getTime } from '../utils/getTime';
import { Breakpoint } from '../utils/breakpoint';
import { CreateClientForm, IFormDataClient } from '../components/create-client-form';
import { timezoneList } from '../assets/timezone';
import { SettingsBlock } from '../components/settings';
import { ConsultationTable } from '../components/consultation/consultation-table';
import { IClient, IConsultation } from '../store/types';
import { Schedule } from '../components/consultation/shedule';
import { ScheduleMobile } from '../components/consultation/shedule/schedule-mobile';
import { ConsultationForm } from '../components/consultation/consultation-form';
import { DeleteConsultationForm } from '../components/delete-form';

const normalizeDateValue = (value?: string) => {
  if (!value) {
    return dayjs().format('YYYY-MM-DD');
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : value;
};

export type FormDataConsultations = {
  id?: number;
  client: string;
  date: string;
  time_start: string;
  time_end: string;
  tz: string;
  link: string;
  tzOffset?: number;
  code?: string;
  therapyType?: 'individual' | 'group';
  groupName?: string;
  participantsCount?: number;
  /** Повтор групповой встречи: не повторять | настроить */
  repeat?: 'no_repeat' | 'configure';
  /** Количество встреч (для групповой с повторением) */
  meetingsCount?: number;
  /** Настройки повторения (при repeat === 'configure') */
  recurrence?: {
    interval: number;
    unit: 'day' | 'week' | 'month';
    weekdays: number[];
    endType: 'never' | 'until_date' | 'after_count';
    endDate?: string;
    endCount?: number;
  };
};

export const defaultFormData: FormDataConsultations = {
  client: '',
  date: dayjs().format('YYYY-MM-DD'),
  time_start: '',
  time_end: '',
  tz: '',
  link: '',
  therapyType: 'individual',
  groupName: '',
  participantsCount: undefined,
  repeat: 'no_repeat' as const,
  meetingsCount: undefined,
  recurrence: undefined,
};

const Consultations = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const userData = useAppSelector(selectUser);
  const pollingOptions = {
    pollingInterval: 30_000,
    refetchOnFocus: true as const,
    refetchOnReconnect: true as const,
  };
  const { data: clients } = useGetClientsQuery({}, pollingOptions);
  const { data: consultations, isLoading: isConsultationsLoading } = useGetConsultationsQuery(
    {
      'date[before]': getDateForFilter(new Date()).end,
      'date[after]': getDateForFilter(new Date()).start,
      'sort[datetime_start]': 'asc',
    },
    pollingOptions,
  );
  const { data: allConsultations } = useGetConsultationsQuery({}, pollingOptions);
  const breakpoint = useBreakpoint();
  const isMobileSchedule = breakpoint <= Breakpoint.Sm;
  const [deleteConsultation] = useDeleteConsultationMutation();
  const [changeClient] = useChangeClientMutation();
  const [createClient] = useCreateClientMutation();
  const captureEvent = usePosthogCapture();
  const [isCreate, setIsCreate] = useState(false);
  const [openCreateClient, setOpenCreateClient] = useState(false);
  const [isEditOnCalendar, setIsEditOnCalendar] = useState(false);
  const [isDeleteOnCalendar, setIsDeleteOnCalendar] = useState(false);
  const [clientsList, setClientsList] = useState<Pick<IClient, 'id' | 'full_name' | 'time_zone'>[]>([]);

  const [consultationList, setConsultationList] = useState<IConsultation[]>([]);
  const [allConsultationList, setAllConsultationList] = useState<IConsultation[]>([]);

  const [openSettings, setOpenSettings] = useState(false);
  const [isReturnForm, setIsReturnForm] = useState(false);
  const [isReturnCalendar, setIsReturnCalendar] = useState(false);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [editedClient, setEditedClient] = useState<IClient | undefined>();

  const [openDelete, setOpenDelete] = useState(false);
  const [deletedItem, setdeletedItem] = useState<IConsultation | null>(null);
  const [formData, setFormData] = useState<FormDataConsultations>(defaultFormData);

  useEffect(() => {
    const state = location.state as { openCalendar?: boolean } | null;
    if (state?.openCalendar) {
      setOpenCalendar(true);
      navigate(location.pathname, { replace: true, state: { ...state, openCalendar: false } });
    }
  }, [location, navigate]);

  const sortConsultationsByEndDate = (list: IConsultation[]) =>
    [...list].sort((a, b) => {
      const firstDate = new Date(a.datetime_end);
      const secondDate = new Date(b.datetime_end);
      const utcFirstDate = new Date(firstDate.getTime() + firstDate.getTimezoneOffset() * 60000);
      const utcSecondDate = new Date(secondDate.getTime() + secondDate.getTimezoneOffset() * 60000);

      if (utcFirstDate > new Date() && utcSecondDate > new Date()) {
        return firstDate.getTime() - secondDate.getTime();
      }
      if (firstDate < new Date() && secondDate < new Date()) {
        return firstDate.getTime() - secondDate.getTime();
      }
      return utcFirstDate > new Date() ? -1 : 1;
    });

  const allConsultationListToday = useMemo(() => sortConsultationsByEndDate(consultationList), [consultationList]);

  const sortConsultation = () => {};

  const handleSaveClient = async (formDataClient: IFormDataClient) => {
    const isEdit = Boolean(editedClient);
    try {
      const data = {
        full_name: formDataClient.name,
        time_zone: formDataClient.tz,
        is_active: formDataClient.active,
        psychologists: [`/api/v1/users/${userData?.id}`],
      };
      if (editedClient) {
        const updatedClient = await changeClient({ ...data, id: editedClient.id }).unwrap();
        setClientsList((prev) =>
          prev.map((client) =>
            client.id === updatedClient.id
              ? { id: updatedClient.id, full_name: updatedClient.full_name, time_zone: updatedClient.time_zone }
              : client,
          ),
        );
        setFormData((prev) => {
          if (!prev.client) {
            return prev;
          }
          const shouldUpdateName = prev.client === editedClient.full_name;
          if (shouldUpdateName) {
            return { ...prev, client: updatedClient.full_name, tz: updatedClient.time_zone };
          }
          return prev;
        });
      }
      if (!editedClient) {
        createClient(data)
          .unwrap()
          .then((res) => {
            const newEl = {
              id: res.id,
              full_name: formDataClient.name,
              time_zone: res.time_zone,
            };
            setOpenCreateClient(false);
            setClientsList((prev) => [...prev, newEl]);
            setFormData((prev) => ({ ...prev, client: newEl.full_name, tz: data.time_zone }));
            setIsCreate(true);
          });
      }
      setOpenCreateClient(false);
      setEditedClient(undefined);
      toast.success(!isEdit ? 'Клиент успешно создан' : 'Клиент успешно отредактирован');
    } catch (error) {
      console.log(error);
    }
  };

  const handleChangeCons = (
    id: number,
    options?: { isEdit?: boolean; overrideStart?: string; overrideEnd?: string; overrideDate?: string },
  ) => {
    const isEdit = options?.isEdit ?? false;
    const current = openCalendar
      ? allConsultationList.find((el) => el.id === id)
      : consultationList.find((el) => el.id === id);
    if (!current) return;
    const client = clientsList.find((el) => `/api/v1/clients/${el.id}` === current.client_entity);
    const tzOffset = timezoneList.find((el) => el.name === current.client_timezone)?.offset;
    const data: FormDataConsultations = {
      id,
      client: client?.full_name ? client?.full_name : '',
      date: normalizeDateValue(options?.overrideDate ?? current.date),
      time_start: options?.overrideStart
        ? options.overrideStart
        : openCalendar && !isEdit
          ? current.datetime_start
          : getTime(new Date(current.datetime_start)),
      time_end: options?.overrideEnd
        ? options.overrideEnd
        : openCalendar && !isEdit
          ? current.datetime_end
          : getTime(new Date(current.datetime_end)),
      tz: current.client_timezone,
      tzOffset: Number(tzOffset),
      link: current.link,
      code: current.code,
      therapyType: current.is_group ? 'group' : 'individual',
      groupName: current.is_group ? (client?.full_name ?? '') : undefined,
    };
    setFormData(data);
    setIsCreate(true);
  };

  const closeClientForm = () => {
    if (!editedClient) {
      setIsCreate(true);
    }
    setEditedClient(undefined);
    setOpenCreateClient(false);
  };

  const closeForm = () => {
    setIsCreate(false);
    setIsEditOnCalendar(false);
    setFormData(defaultFormData);
  };

  const openForm = (options?: { date?: string; start?: string; end?: string }) => {
    setFormData({
      id: undefined,
      client: '',
      date: normalizeDateValue(options?.date),
      time_start: options?.start ?? '',
      time_end: options?.end ?? '',
      tz: '(GMT +03:00) Москва',
      link: `${import.meta.env.VITE_REACT_APP_VIDEOROOM_URL}/meet/${randomString(5)}`,
      tzOffset: undefined,
      code: undefined,
      therapyType: 'individual',
      groupName: '',
      participantsCount: undefined,
      repeat: 'no_repeat',
      meetingsCount: undefined,
    });
    setIsCreate(true);
    setIsEditOnCalendar(true);
  };

  const handleOpenSettings = () => {
    if (openCalendar) {
      if (formData.id) {
        setIsReturnForm(true);
      }
      setIsReturnCalendar(true);
      setOpenCalendar(false);
    } else {
      setIsReturnForm(true);
    }
    setIsCreate(false);
    setOpenSettings(true);
  };

  const openEditFormClient = (id: number) => {
    const curr = clients?.find((el) => el.id === id);
    if (curr) {
      setEditedClient(curr);
      setOpenCreateClient(true);
    }
  };

  const handleOpenDelete = (item: IConsultation, isCalendar: boolean) => {
    setOpenDelete(!openDelete);
    setdeletedItem(item);
    setIsDeleteOnCalendar(isCalendar);
  };

  const closeDeleteForm = () => {
    setOpenDelete(false);
    setdeletedItem(null);
    if (!isDeleteOnCalendar) {
      document.body.classList.remove('hidden_scroll');
    }
    setIsDeleteOnCalendar(false);
  };

  const handleDelete = (id: number, scope?: 'series' | 'this_and_future') => {
    const wasCalendarDeletion = isDeleteOnCalendar;
    const deletedConsultation = deletedItem;
    deleteConsultation({ id, scope })
      .unwrap()
      .then(() => {
        if (!scope) {
          setConsultationList((prev) => prev.filter((c) => c.id !== id));
          setAllConsultationList((prev) => prev.filter((c) => c.id !== id));
        }
        closeDeleteForm();
        if (wasCalendarDeletion) {
          captureEvent('calendar_entity_deleted', {
            consultation_id: id,
            scope: scope ?? 'one',
            client_entity: deletedConsultation?.client_entity,
            client_timezone: deletedConsultation?.client_timezone,
            datetime_start: deletedConsultation?.datetime_start,
            datetime_end: deletedConsultation?.datetime_end,
          });
        }
        toast.success(
          scope === 'series'
            ? 'Серия встреч удалена'
            : scope === 'this_and_future'
              ? 'Встреча и последующие удалены'
              : 'Встреча успешно удалена',
        );
      })
      .catch(() => {
        toast.error('Произошла ошибка при удалении встречи');
      });
  };

  const handleCalendarReschedule = ({
    id,
    newStart,
    newEnd,
    originalStart,
    originalEnd,
  }: {
    id: number;
    newStart: string;
    newEnd: string;
    originalStart: string;
    originalEnd: string;
  }) => {
    const startDayjs = dayjs(newStart);
    const endDayjs = dayjs(newEnd);
    setIsEditOnCalendar(true);
    handleChangeCons(id, {
      isEdit: true,
      overrideDate: startDayjs.startOf('day').toISOString(),
      overrideStart: startDayjs.format('HH:mm'),
      overrideEnd: endDayjs.format('HH:mm'),
    });
    captureEvent('calendar_entity_rescheduled', {
      consultation_id: id,
      original_start: originalStart,
      original_end: originalEnd,
      new_start: newStart,
      new_end: newEnd,
    });
  };

  useEffect(() => {
    if (allConsultations) {
      setAllConsultationList(allConsultations);
    }
  }, [allConsultations]);

  useEffect(() => {
    if (consultations) {
      setConsultationList(consultations);
    }
  }, [consultations]);

  useEffect(() => {
    if (clients) {
      const arr = clients.map((el) => {
        return {
          id: el.id,
          full_name: el.full_name,
          time_zone: el.time_zone,
        };
      });
      setClientsList(arr);
    }
  }, [clients]);

  return (
    <div className="flex flex-col relative h-auto w-full">
      {openCalendar ? (
        isMobileSchedule ? (
          <ScheduleMobile
            handleChangeCons={handleChangeCons}
            consultations={allConsultationList}
            clients={clientsList}
            onRescheduleRequest={handleCalendarReschedule}
            onCreateFromSlot={({ date, start, end }) => {
              openForm({ date, start, end });
            }}
            close={() => setOpenCalendar(false)}
            openCreateForm={() => openForm()}
          />
        ) : (
          <Schedule
            handleChangeCons={handleChangeCons}
            consultations={allConsultationList}
            clients={clientsList}
            onRescheduleRequest={handleCalendarReschedule}
            onCreateFromSlot={({ date, start, end }) => {
              openForm({ date, start, end });
            }}
            close={() => setOpenCalendar(false)}
            openCreateForm={() => openForm()}
            handleOpenDelete={handleOpenDelete}
          />
        )
      ) : (
        <ConsultationTable
          openEditFormClient={openEditFormClient}
          openForm={openForm}
          setOpenSettings={setOpenSettings}
          setOpenCalendar={setOpenCalendar}
          handleChangeCons={handleChangeCons}
          consultationList={allConsultationListToday || []}
          isLoading={isConsultationsLoading}
          clientsList={clientsList}
          sortConsultation={sortConsultation}
          handleOpenDelete={handleOpenDelete}
        />
      )}

      <ConsultationForm
        isCreate={isCreate}
        setIsCreate={setIsCreate}
        isEditOnCalendar={isEditOnCalendar}
        openCalendar={openCalendar}
        formData={formData}
        setFormData={setFormData}
        consultationList={consultationList}
        allConsultationList={allConsultationList}
        clientsList={clientsList}
        clients={clients || []}
        closeForm={closeForm}
        handleOpenSettings={handleOpenSettings}
        handleChangeCons={handleChangeCons}
        setIsEditOnCalendar={setIsEditOnCalendar}
        setConsultationList={setConsultationList}
        setAllConsultationList={setAllConsultationList}
        handleOpenDelete={handleOpenDelete}
        openEditFormClient={openEditFormClient}
      />
      <CreateClientForm
        clientData={editedClient}
        open={openCreateClient}
        close={closeClientForm}
        save={(form) => handleSaveClient(form)}
      />
      <DeleteConsultationForm
        client={clientsList.find((el) => `/api/v1/clients/${el.id}` === deletedItem?.client_entity)}
        data={deletedItem}
        open={openDelete}
        close={closeDeleteForm}
        deleteConsultation={handleDelete}
        isFromCalendar={isDeleteOnCalendar}
      />

      {openSettings && (
        <SettingsBlock
          closeSettings={() => {
            if (isReturnCalendar) {
              if (isReturnForm) {
                setIsCreate(true);
              }
              setOpenCalendar(true);
              setOpenSettings(false);
              setIsReturnCalendar(false);

              return;
            }
            if (isReturnForm) {
              setIsCreate(true);
              setOpenSettings(false);
              setIsReturnForm(false);

              return;
            }
            setOpenSettings(false);
          }}
        />
      )}
    </div>
  );
};

export default Consultations;
